/* eslint-disable @typescript-eslint/no-non-null-assertion */
import type { FormFieldProps } from "@octopusdeploy/design-system-octopus-components";
import { TextWithLabel as Text, DeprecatedSelect } from "@octopusdeploy/design-system-octopus-components";
import type { TagSetResource, TagResource } from "@octopusdeploy/octopus-server-client";
import type * as React from "react";
import { useMemo } from "react";
import type { DoBusyTask } from "~/components/DataBaseComponent/DataBaseComponent";
import { DeprecatedMultiSelect } from "~/components/MultiSelect/MultiSelect";
import type { SelectItem } from "~/components/VirtualListWithKeyboard/SelectItem";
import Tag from "../../Tag";
interface TagSetSelectProps extends FormFieldProps<string[]> {
    TagSet: TagSetResource;
    doBusyTask: DoBusyTask;
    disabled?: boolean;
    autoFocus?: boolean;
    error?: string;
    warning?: string;
}
const TagResourceMultiSelect = DeprecatedMultiSelect<TagResource>();
export const TagSetSelect: React.FC<TagSetSelectProps> = ({ TagSet, value, onChange, disabled, autoFocus, error, warning, doBusyTask, ...rest }) => {
    const TagSetItems = useMemo(() => {
        if (!TagSet.Tags || TagSet.Tags.length === 0) {
            return [];
        }
        return TagSet.Tags.sort((a, b) => a.SortOrder - b.SortOrder).map((TagSetValue) => ({
            value: TagSetValue.Id,
            text: TagSetValue.Name,
            description: TagSetValue.Description,
            color: TagSetValue.Color,
        }));
    }, [TagSet.Tags]);
    const chipRenderer = (tagSet: TagSetResource, r: TagResource | SelectItem, onRequestDelete: () => void) => {
        const tag = tagSet.Tags.find((t) => t.Id === r.Id);
        return <Tag onRequestDelete={onRequestDelete} deleteButtonAccessibleName={`Delete ${tag!.Name}`} description={tag!.Description} tagName={tag!.Name} tagColor={tag!.Color}/>;
    };
    switch (TagSet.Type) {
        case "SingleSelect":
            return (<DeprecatedSelect items={TagSetItems.map((item) => ({
                    value: item.value,
                    text: item.text,
                    description: item.description,
                }))} value={value.length > 0 ? TagSet.Tags.find((t) => t.CanonicalTagName === value[0])?.Id || "" : ""} onChange={(newValue) => {
                    onChange?.(newValue ? [newValue] : []);
                }} disabled={disabled} allowFilter={true} allowClear={true} autoFocus={autoFocus} label={`${TagSet.Name} (Single Select)`} error={error} warning={warning} sortItems={false} // Maintain sort order of items passed through
             {...rest}/>);
        case "MultiSelect":
            return (<TagResourceMultiSelect accessibleName={`tagSetFilter-${TagSet.Name}`} onChange={(canonicalTagIds) => {
                    onChange?.(canonicalTagIds);
                }} key={TagSet.Id} value={value ? TagSet.Tags.filter((t) => value!.indexOf(t.CanonicalTagName) !== -1).map((t) => t.Id) : []} items={TagSet.Tags ? TagSet.Tags.sort((t1, t2) => t1.SortOrder - t2.SortOrder) : null!} renderChip={(r: TagResource | SelectItem, dc) => chipRenderer(TagSet, r, dc)} fieldName={`${TagSet.Name} tags`} description={TagSet.Description} autoFocus={autoFocus} sortItems={false} // Maintain sort order of items passed through
            />);
        case "FreeText":
        default:
            return (<Text value={value.length > 0 ? value[0].replace(`${TagSet.Name}/`, "") : ""} onChange={(newValue) => {
                    const syntheticId = newValue ? `${TagSet.Name}/${newValue}` : "";
                    onChange?.(syntheticId ? [syntheticId] : []);
                }} label={`${TagSet.Name} (Free Text)`} disabled={disabled} autoFocus={autoFocus} error={error} warning={warning} {...rest}/>);
    }
};
TagSetSelect.displayName = "TagSetSelect"
export default TagSetSelect;
